"use client";

import { cn } from "@/utils/cn";
import { Children, PointerEvent, ReactNode, useRef, useState } from "react";
import Reveal, { RevealGroup } from "../Motion/Reveal";
import SliderArrow from "./SliderArrow";

interface Props {
	// Заголовок секции: стрелки прокрутки стоят с ним в одной строке
	heading: ReactNode;
	children: ReactNode;
}

interface DragState {
	startX: number;
	startScroll: number;
	hasMoved: boolean;
}

// idle — обычный скролл со snap; dragging — ленту тянут мышью;
// settling — после отпускания лента доезжает до слайда
type DragPhase = "idle" | "dragging" | "settling";

// Сдвиг мыши, после которого нажатие считается перетаскиванием
const DRAG_THRESHOLD = 5;
// Запас на доводку ленты для браузеров без события scrollend
const SETTLE_FALLBACK_MS = 800;
// Карточки выезжают по очереди; тем, что за краем экрана, очередь не нужна
const MAX_STAGGER = 3;
// Затемнение у края ленты — намёк, что за ним есть ещё карточки
const EDGE_FADE =
	"pointer-events-none absolute inset-y-0 w-11 from-background/0 via-background/68 to-background transition-opacity lg:w-26";

export default function ServicesSlider({ heading, children }: Props) {
	const slides = Children.toArray(children);
	const trackRef = useRef<HTMLUListElement>(null);
	const dragRef = useRef<DragState | null>(null);
	const settleTimerRef = useRef<number | undefined>(undefined);
	const [activeIndex, setActiveIndex] = useState(0);
	const [isAtStart, setIsAtStart] = useState(true);
	const [isAtEnd, setIsAtEnd] = useState(false);
	const [dragPhase, setDragPhase] = useState<DragPhase>("idle");

	const getSlideStep = (track: HTMLUListElement) => {
		const slide = track.firstElementChild;
		if (!(slide instanceof HTMLElement)) return 0;

		const gap = parseFloat(getComputedStyle(track).columnGap) || 0;

		return slide.offsetWidth + gap;
	};

	const scrollToSlide = (index: number) => {
		const track = trackRef.current;
		if (!track) return;

		track.scrollTo({ left: index * getSlideStep(track), behavior: "smooth" });
	};

	// Считаем от реальной позиции ленты: в конце она стоит между слайдами
	const scrollBySlide = (direction: -1 | 1) => {
		const track = trackRef.current;
		if (!track) return;

		const position = track.scrollLeft / (getSlideStep(track) || 1);

		scrollToSlide(
			direction > 0
				? Math.floor(position + 0.01) + 1
				: Math.ceil(position - 0.01) - 1
		);
	};

	const handleScroll = () => {
		const track = trackRef.current;
		if (!track) return;

		const step = getSlideStep(track);
		const atEnd =
			track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;

		setIsAtStart(track.scrollLeft <= 1);
		setIsAtEnd(atEnd);
		if (atEnd) setActiveIndex(slides.length - 1);
		else if (step) setActiveIndex(Math.round(track.scrollLeft / step));
	};

	// Перетаскивание нужно только мыши: палец и тачпад листают ленту нативно
	const handlePointerDown = (event: PointerEvent<HTMLUListElement>) => {
		if (event.pointerType !== "mouse" || event.button !== 0) return;

		dragRef.current = {
			startX: event.clientX,
			startScroll: event.currentTarget.scrollLeft,
			hasMoved: false,
		};
	};

	const handlePointerMove = (event: PointerEvent<HTMLUListElement>) => {
		const drag = dragRef.current;
		if (!drag) return;

		const offset = event.clientX - drag.startX;

		if (!drag.hasMoved) {
			if (Math.abs(offset) < DRAG_THRESHOLD) return;

			drag.hasMoved = true;
			event.currentTarget.setPointerCapture(event.pointerId);
			window.clearTimeout(settleTimerRef.current);
			setDragPhase("dragging");
		}

		event.currentTarget.scrollLeft = drag.startScroll - offset;
	};

	const finishSettling = () => {
		window.clearTimeout(settleTimerRef.current);
		setDragPhase((phase) => (phase === "settling" ? "idle" : phase));
	};

	const handlePointerUp = (event: PointerEvent<HTMLUListElement>) => {
		const drag = dragRef.current;
		dragRef.current = null;
		if (!drag?.hasMoved) return;

		// Доводим ленту до ближайшего слайда в сторону движения, snap вернётся
		// после остановки — иначе браузер перебьёт плавную прокрутку рывком
		const track = event.currentTarget;
		const position = track.scrollLeft / (getSlideStep(track) || 1);
		const isForward = event.clientX < drag.startX;

		setDragPhase("settling");
		scrollToSlide(isForward ? Math.ceil(position) : Math.floor(position));
		settleTimerRef.current = window.setTimeout(
			finishSettling,
			SETTLE_FALLBACK_MS
		);
	};

	return (
		<div className="flex flex-col gap-6 lg:gap-10">
			<div className="flex items-end justify-between gap-6">
				{heading}

				<div className="flex gap-2 max-lg:hidden">
					<SliderArrow
						direction="prev"
						isDisabled={isAtStart}
						onClick={() => scrollBySlide(-1)}
					/>
					<SliderArrow
						direction="next"
						isDisabled={isAtEnd}
						onClick={() => scrollBySlide(1)}
					/>
				</div>
			</div>

			<RevealGroup className="relative -mx-5 lg:-mx-18">
				<ul
					ref={trackRef}
					// Лента листается и с клавиатуры: фокус на ней, дальше стрелки
					tabIndex={0}
					aria-label="Услуги"
					onScroll={handleScroll}
					onScrollEnd={finishSettling}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerUp}
					onPointerCancel={handlePointerUp}
					className={cn(
						"-mb-8 flex cursor-grab scroll-pl-5 gap-4 overflow-x-auto px-5 pb-8 outline-primary [scrollbar-width:none] focus-visible:outline-2 focus-visible:-outline-offset-2 lg:scroll-pl-18 lg:gap-6 lg:px-18 [&::-webkit-scrollbar]:hidden",
						dragPhase === "idle" ? "snap-x snap-mandatory" : "snap-none",
						dragPhase === "dragging" && "cursor-grabbing select-none"
					)}
				>
					{slides.map((slide, index) => (
						<Reveal
							key={index}
							as="li"
							inGroup
							delay={Math.min(index, MAX_STAGGER) * 0.12}
							className="shrink-0 snap-start"
						>
							{slide}
						</Reveal>
					))}
				</ul>

				<div
					className={cn(
						EDGE_FADE,
						"left-0 bg-linear-to-l",
						isAtStart && "opacity-0"
					)}
				/>
				<div
					className={cn(
						EDGE_FADE,
						"right-0 bg-linear-to-r",
						isAtEnd && "opacity-0"
					)}
				/>
			</RevealGroup>

			{/* Кнопка крупнее видимой точки, чтобы в неё попадал палец */}
			<div className="-my-2 flex justify-center lg:hidden">
				{slides.map((_, index) => (
					<button
						key={index}
						type="button"
						aria-label={`Услуга ${index + 1}`}
						aria-current={index === activeIndex}
						onClick={() => scrollToSlide(index)}
						className="flex size-6 items-center justify-center"
					>
						<span
							className={cn(
								"size-2 rounded-full transition-colors",
								index === activeIndex ? "bg-primary" : "bg-stroke"
							)}
						/>
					</button>
				))}
			</div>
		</div>
	);
}
